import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Bar } from "recharts";
import { BarChart, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

const objects = ["Apples", "Balloons", "Cars", "Books", "Toys", "Candies", "Balls", "Flowers", "Stars", "Fish"];

const VerticalBarGraph: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [questions, setQuestions] = useState<any[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Generate random bar graph data
    const generateBarGraphData = () => {
        const selectedObjects = [];
        const usedIndices = new Set<number>();

        while (selectedObjects.length < 4) {
            const randomIndex = Math.floor(Math.random() * objects.length);
            const quantity = Math.floor(Math.random() * 10) + 1; // Quantity between 1 and 10

            if (!usedIndices.has(randomIndex)) {
                selectedObjects.push({ object: objects[randomIndex], quantity });
                usedIndices.add(randomIndex);
            }
        }
        return selectedObjects;
    };

    // Generate dynamic questions based on the data
    const generateQuestions = (barGraphData: any[]) => {
        const total = barGraphData.reduce((acc, item) => acc + item.quantity, 0);

        return [
            { question: `How many ${barGraphData[0].object} are there?`, answer: barGraphData[0].quantity },
            { question: `Which object has the highest quantity?`, answer: barGraphData.reduce((max, item) => item.quantity > max.quantity ? item : max).object },
            { question: `What is the total number of objects?`, answer: total }
        ];
    };

    useEffect(() => {
        handleQuestion();
        document.title = "Bar Graph – Fun Data Handling Activity for UKG Kids | Mama’s Guidance";
    }, []);

    const handleQuestion = () => {
        const newData = generateBarGraphData();
        setData(newData);
        setQuestions(generateQuestions(newData));
        setCurrentQuestionIndex(Math.floor(Math.random() * 3));
        setSelectedOption(null);
        setShowImage(false);
    };

    const handleAnswerSubmit = (userAnswer: any) => {
        setSelectedOption(userAnswer);
        const correctAnswer = questions[currentQuestionIndex].answer;
        if (userAnswer === correctAnswer) {
            setShowImage(true);
            setTimeout(handleQuestion, 3000);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Introduce UKG children to bar graphs through colorful worksheets. Learn simple data handling, comparison, and counting using engaging visuals." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/data-handling/bar-graph" />
            </Helmet>
            <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                {!showImage ? (
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Bar Graph</h1>

                                <p>
                                    Learning to organize and interpret information is an essential early math skill. In this <strong>“Bar Graph”</strong> activity, UKG students will explore data handling by counting objects, recording simple data, and identifying the tallest or shortest bars on a graph. It&apos;s visual, interactive, and perfect for young learners!
                                </p>

                                <h2>📊 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Understanding what a bar graph is</li>
                                    <li>Reading data presented in bar format</li>
                                    <li>Comparing quantities visually</li>
                                    <li>Answering simple questions from the graph (e.g., “Which bar is tallest?”)</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Develop observation and comparison skills</li>
                                    <li>Practice basic counting and classification</li>
                                    <li>Begin understanding the concept of data representation</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>💡 Activities for Home or School</h2>
                                <ul>
                                    <li>Use toy fruits or objects to create a real bar graph on a board</li>
                                    <li>Count items in the house (socks, spoons, books) and draw your own graph</li>
                                    <li>Ask comparison questions like “Which item do we have the most of?”</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Bar Graph</h1>

                            <p>
                                Learning to organize and interpret information is an essential early math skill. In this <strong>“Bar Graph”</strong> activity, UKG students will explore data handling by counting objects, recording simple data, and identifying the tallest or shortest bars on a graph. It&apos;s visual, interactive, and perfect for young learners!
                            </p>

                            <h2>📊 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Understanding what a bar graph is</li>
                                <li>Reading data presented in bar format</li>
                                <li>Comparing quantities visually</li>
                                <li>Answering simple questions from the graph (e.g., “Which bar is tallest?”)</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Develop observation and comparison skills</li>
                                <li>Practice basic counting and classification</li>
                                <li>Begin understanding the concept of data representation</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>💡 Activities for Home or School</h2>
                            <ul>
                                <li>Use toy fruits or objects to create a real bar graph on a board</li>
                                <li>Count items in the house (socks, spoons, books) and draw your own graph</li>
                                <li>Ask comparison questions like “Which item do we have the most of?”</li>
                            </ul>
                        </article>
                        <ResponsiveContainer className="barGraph" height={300}>
                            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="object" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="quantity" fill="#8884d8" barSize={50} />
                            </BarChart>
                        </ResponsiveContainer>
                        <div>
                            <h3>Question:</h3>
                            <p>{questions[currentQuestionIndex]?.question}</p>
                        </div>
                        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                            {typeof questions[currentQuestionIndex]?.answer === "number" ? (
                                [questions[currentQuestionIndex].answer - 1, questions[currentQuestionIndex].answer, questions[currentQuestionIndex].answer + 1]
                                    .sort(() => Math.random() - 0.5) // Shuffle options
                                    .map((option, index) => (
                                        <button key={index} className="background-blue"
                                            style={{ padding: "8px", backgroundColor: selectedOption === option ? (option === questions[currentQuestionIndex].answer ? "green" : "red") : "", cursor: "pointer", width: "75px" }}
                                            onClick={() => handleAnswerSubmit(option)}
                                        >
                                            {option}
                                        </button>
                                    ))
                            ) : (
                                data.map((item, index) => (
                                    <button key={index} className="background-blue"
                                        style={{ padding: "10px", backgroundColor: selectedOption === item.object ? (item.object === questions[currentQuestionIndex].answer ? "green" : "red") : "", cursor: "pointer" }}
                                        onClick={() => handleAnswerSubmit(item.object)}
                                    >
                                        {item.object}
                                    </button>
                                ))
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <h2>🎉 Well Done! 🎉</h2>
                        <p>Lets try another one!</p>
                    </>
                )}
            </div>
        </>
    );
};

export default VerticalBarGraph;
